import {
  NumericFormatType,
  ReportSection,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationControlType,
  ReportSectionDataSection,
  ReportType,
  ReportViewModelSection
} from '@capital-access/reporting/common';
import { FundSummaryModel } from './fund-summary.model';

export class FundsSummaryConfiguration extends ReportSectionConfigurationBase {
  typeName = 'FundsSummaryConfiguration';
  nodataLocalizationKeyName = 'fundsSummary';
  readonly reportSection = ReportSection.FundSummary;
  readonly applicableReportTypes = [ReportType.Contact, ReportType.Institution];
  readonly reportViewModelSections = [ReportViewModelSection.InvestorProfile_FundSummary];
  override canSelectAllDataSections = false;
  override maxGridUnitsAllowed = 21.5;

  get minSize(): number {
    const totalSelectedUnits = this.availableDataSections
      .filter(section => section.isSelected)
      .reduce((sum, section) => sum + (section.unit || 0), 0);
    if (totalSelectedUnits > 21.5) {
      throw new Error('Too many columns selected');
    } else if (totalSelectedUnits > 16) {
      return 4;
    } else if (totalSelectedUnits > 10.5) {
      return 3;
    } else {
      return 2;
    }
  }

  maxSize = 4;
  defaultSize = 4;
  availableDataSections: ReportSectionDataSection[] = [
    {
      name: 'fundsSummaryFundName',
      displayName: 'Fund Name',
      isSelected: true,
      canConfigure: false,
      unit: 6,
      property: 'name',
      isStatic: true,
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'fundsSummaryShares',
      displayName: 'Shares',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'shares',
      type: ReportSectionConfigurationControlType.BigNumber
    },
    {
      name: 'fundsSummaryShareChange',
      displayName: 'Share Change',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'sharesChange',
      type: ReportSectionConfigurationControlType.BigNumber
    },
    {
      name: 'fundsSummaryReportedDate',
      displayName: 'Reported Date',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'reportedDate',
      type: ReportSectionConfigurationControlType.Date
    },
    {
      name: 'fundsSummarySuitabilityScore',
      displayName: 'Suitability Score',
      isSelected: true,
      canConfigure: true,
      unit: 1.5,
      property: 'suitabilityScore',
      type: ReportSectionConfigurationControlType.Number
    },
    {
      name: 'fundsSummaryPurchasingPower',
      displayName: 'Purchasing Power',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'purchasingPower',
      type: ReportSectionConfigurationControlType.BigNumber
    },
    {
      name: 'fundsSummaryEquityAssets',
      displayName: 'Equity Assets',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'equityAssets',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.InMillions
    },
    {
      name: 'fundsSummaryTurnover',
      displayName: 'Turnover',
      isSelected: true,
      canConfigure: true,
      unit: 2,
      property: 'turnover',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'fundsSummaryFundType',
      displayName: 'Fund Type',
      isSelected: false,
      canConfigure: true,
      unit: 2,
      property: 'investorTypeName',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'fundsSummaryPctSo',
      displayName: '% S/O',
      isSelected: false,
      canConfigure: true,
      unit: 1,
      property: 'sharesOutstandingPct',
      type: ReportSectionConfigurationControlType.Decimal,
      numericFormatType: NumericFormatType.Percentage
    },
    {
      name: 'fundsSummaryHolderRank',
      displayName: 'Fund Holder Rank',
      isSelected: false,
      canConfigure: true,
      unit: 2,
      property: 'holderRank',
      type: ReportSectionConfigurationControlType.Number
    },
    {
      name: 'fundsSummaryCountryExposure',
      displayName: 'Country Exposure',
      isSelected: false,
      canConfigure: true,
      unit: 1.5,
      property: 'countryExposure',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'fundsSummaryMarketCapExposure',
      displayName: 'Market Cap Exposure',
      isSelected: false,
      canConfigure: true,
      unit: 1.5,
      property: 'marketCapExposure',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'fundsSummaryIndustryExposure',
      displayName: 'Industry Exposure',
      isSelected: false,
      canConfigure: true,
      unit: 1.5,
      property: 'industryExposure',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'fundsSummaryStyle',
      displayName: 'Style',
      isSelected: false,
      canConfigure: true,
      unit: 2,
      property: 'styleName',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'fundsSummaryOrientation',
      displayName: 'Orientation',
      isSelected: false,
      canConfigure: true,
      unit: 2,
      property: 'orientation',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'fundsSummarySfdr',
      displayName: 'SFDR',
      isSelected: false,
      canConfigure: true,
      applicableReportTypes: [ReportType.Institution, ReportType.Contact],
      unit: 1.5,
      property: 'sfdr',
      icon: 'f-i f-i-sm f-i-info',
      type: ReportSectionConfigurationControlType.Text
    },
    {
      name: 'fundsSummaryEsgWam',
      displayName: 'Fund ESG WAM',
      isSelected: false,
      canConfigure: true,
      applicableReportTypes: [ReportType.Institution, ReportType.Contact],
      unit: 1.5,
      property: 'fundWam',
      icon: 'f-i f-i-sm f-i-info',
      type: ReportSectionConfigurationControlType.Number
    },
    {
      name: 'fundsSummaryPortfolioManager',
      displayName: 'Managed By/Portfolio Manager',
      isSelected: true,
      canConfigure: true,
      property: 'portfolioManagers',
      type: ReportSectionConfigurationControlType.Custom
    }
  ];

  get name(): string {
    if (this.appliedReportType === ReportType.Contact) {
      return 'contactFundsSummary';
    } else if (this.appliedReportType === ReportType.Institution) {
      return 'institutionFundsSummary';
    } else {
      throw new Error('Report Type not supported');
    }
  }

  get displayName(): string {
    if (this.appliedReportType === ReportType.Contact || this.appliedReportType === ReportType.Institution) {
      return 'Funds Summary';
    } else {
      throw new Error('Report Type not supported');
    }
  }
}

export interface FundsSummaryDetailsData {
  [ReportViewModelSection.InvestorProfile_FundSummary]: FundSummaryModel;
}
